<template>
    <div ref="block" class="refund-details-block">
        <h3 class="pb-section-heading block-title">Canceling your plan today means:</h3>

        <div class="loss-cards">
            <!-- The people riding on this subscription come first: they lose the most, and it is
                 the one loss here the member chose for someone else. Their card's place is held
                 while the read of who they are is out, so the cards below stay where they land.
                 The status region mounts empty, since one that appears already holding its text is
                 not reliably announced, and says the read is out once it has mounted. -->
            <span class="sr-only" role="status">{{ readingStatus }}</span>
            <!-- A failed read is said in the card's place, with the way to read again, and holds
                 Continue: the people come first. Try again says the failure, and hands focus to the
                 first acknowledgement left once its read lands. -->
            <section v-if="dependentsUnread" class="loss-card" data-testid="refund-dependents-unread">
                <div class="loss-card__band" aria-hidden="true">
                    <PhUsersThree :size="32" weight="regular" />
                </div>
                <div class="loss-card__body loss-card__body--unread">
                    <p class="loss-card__title">{{ DEPENDENTS_UNREAD }}</p>
                    <RetryButton
                        :busy="readingDependents"
                        :failure="DEPENDENTS_UNREAD"
                        :focus-target="firstAcknowledgement"
                        data-testid="refund-dependents-retry"
                        @retry="readDependents"
                    />
                </div>
            </section>
            <div v-else-if="readingDependents" class="loss-card" aria-hidden="true" data-testid="refund-dependents-loading">
                <PrimeSkeleton class="loss-card__band-placeholder" height="4rem" />
                <div class="loss-card__body">
                    <PrimeSkeleton width="40%" height="var(--leading-sm)" />
                    <PrimeSkeleton height="4rem" />
                    <PrimeSkeleton width="90%" height="var(--leading-sm)" />
                </div>
            </div>
            <section v-else-if="dependents.length > 0" class="loss-card" data-testid="refund-dependents-card">
                <div class="loss-card__band" aria-hidden="true">
                    <PhUsersThree :size="32" weight="regular" />
                </div>
                <div class="loss-card__body">
                    <p :id="ids.people" class="loss-card__title" data-testid="refund-dependents-title">{{ revokingStatement(dependents) }}</p>
                    <GiftDependentsList :dependents="dependents" />
                    <p class="pb-supporting loss-card__note">{{ DEPENDENTS_LOSE }}</p>
                    <label class="loss-card__check">
                        <PrimeCheckbox
                            v-model="understood.people"
                            binary
                            :aria-labelledby="`${ids.people} ${ids.understand}-people`"
                            data-testid="refund-dependents-understood"
                        />
                        <span :id="`${ids.understand}-people`">I understand</span>
                    </label>
                </div>
            </section>

            <div class="loss-card-row">
                <section class="loss-card">
                    <div class="loss-card__band" aria-hidden="true">
                        <svg class="loss-card__glyph" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="loss-card__body">
                        <p :id="ids.services" class="loss-card__title">
                            You will end these services, which are currently working to protect your privacy:
                        </p>
                        <ul class="loss-card__list">
                            <li v-for="benefit in (licenseBenefits || []).slice(1, 5)" :key="benefit.id">{{ benefit.text }}</li>
                        </ul>
                        <label class="loss-card__check">
                            <PrimeCheckbox
                                v-model="understood.services"
                                binary
                                :aria-labelledby="`${ids.services} ${ids.understand}-services`"
                            />
                            <span :id="`${ids.understand}-services`">I understand</span>
                        </label>
                    </div>
                </section>

                <section class="loss-card">
                    <div class="loss-card__band" aria-hidden="true">
                        <svg class="loss-card__glyph" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="loss-card__body">
                        <p :id="ids.features" class="loss-card__title">
                            You will lose access to these privacy-enhancing features:
                        </p>
                        <ul class="loss-card__list">
                            <li v-for="benefit in (licenseBenefits || []).slice(5, 9)" :key="benefit.id">{{ benefit.text }}</li>
                        </ul>
                        <label class="loss-card__check">
                            <PrimeCheckbox
                                v-model="understood.features"
                                binary
                                :aria-labelledby="`${ids.features} ${ids.understand}-features`"
                            />
                            <span :id="`${ids.understand}-features`">I understand</span>
                        </label>
                    </div>
                </section>

                <section class="loss-card">
                    <div class="loss-card__band" aria-hidden="true">
                        <svg class="loss-card__glyph" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="loss-card__body">
                        <p :id="ids.removals" class="loss-card__title">
                            All the removal requests and opt-outs we're managing for you will expire, so you will likely start seeing:
                        </p>
                        <ul class="loss-card__list">
                            <li v-for="benefit in (licenseBenefits || []).slice(9)" :key="benefit.id">{{ benefit.text }}</li>
                            <template v-if="(licenseBenefits || []).length <= 9">
                                <li>Increased telemarketing calls</li>
                                <li>More marketing SMS/texts</li>
                                <li>Your sensitive contact info being publicly exposed</li>
                            </template>
                        </ul>
                        <label class="loss-card__check">
                            <PrimeCheckbox
                                v-model="understood.removals"
                                binary
                                :aria-labelledby="`${ids.removals} ${ids.understand}-removals`"
                            />
                            <span :id="`${ids.understand}-removals`">I understand</span>
                        </label>
                    </div>
                </section>
            </div>
        </div>

        <div class="action-buttons">
            <PrimeButton label="Back" variant="secondary" @click="emit('goToPage', 1)" />
            <PrimeButton
                label="Continue"
                :disabled="!allowedToContinue"
                :data-busy="readingDependents ? '' : undefined"
                data-testid="refund-details-continue"
                @click="continueToNext"
            >
                <template #icon>
                    <PbScanMark v-if="readingDependents" />
                </template>
            </PrimeButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { PhUsersThree } from '@phosphor-icons/vue';
import GiftDependentsList from 'pb-billing-ui/GiftDependentsList.vue';
import { DEPENDENTS_LOSE, DEPENDENTS_UNREAD, revokingStatement, type GiftDependent } from 'pb-billing-ui/gift-dependents.ts';
import PrimeButton from 'primevue/button';
import PrimeCheckbox from 'primevue/checkbox';
import PrimeSkeleton from 'primevue/skeleton';
import { computed, onMounted, reactive, ref, useId } from 'vue';
import RetryButton from '~/components/shared/RetryButton.vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { DEPENDENTS_READING, useGiftDependents } from '~/composables/useGiftDependents';
import { buildLicenseBenefits, LicenseType } from '~/types/licenses/licenses';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.REFUND_LICENSE);

const emit = defineEmits<{
    goToPage: [page: number];
    close: [];
}>();

const idBase = useId();
const ids = {
    people: `${idBase}-people`,
    services: `${idBase}-services`,
    features: `${idBase}-features`,
    removals: `${idBase}-removals`,
    understand: `${idBase}-understand`,
};

const understood = reactive({ people: false, services: false, features: false, removals: false });

const { stats } = useBrokerStats();

/** The three consumer plans, the only ones this list describes. */
const isConsumerPlan = (slug: string): slug is LicenseType => (Object.values(LicenseType) as string[]).includes(slug);

// What this plan buys, priced off the shared coverage counts, so the list a
// member reads while deciding to cancel carries the same figures as the sidebar
// behind it, and never a 0. A business seat is refunded through its company
// rather than this dialog, so it has no list here and the template renders none.
const licenseBenefits = computed(() => {
    const slug = dialogRef.value.data.slug;

    return isConsumerPlan(slug) ? buildLicenseBenefits(stats.value)[slug] : undefined;
});

const { sponsorsDependents, dependentsOn } = useGiftDependents();

const block = ref<HTMLElement | null>(null);

/** The people whose protection this refund ends, in the order they accepted. */
const dependents = ref<GiftDependent[]>([]);
// Only a seat carrying the member's gifts has people on it to read. Continue waits for the read, so
// the people card cannot arrive after the member has gone on.
const readingDependents = ref(sponsorsDependents(dialogRef.value.data));
/** What the status region says about the read: set by each read, never as the region mounts. */
const readingStatus = ref('');
/** The last read of who depends on the plan failed, and no read since has landed. */
const dependentsUnread = ref(false);

async function readDependents(): Promise<void> {
    readingDependents.value = true;
    readingStatus.value = DEPENDENTS_READING;
    const read = await dependentsOn(dialogRef.value.data);
    readingDependents.value = false;
    readingStatus.value = '';
    dependentsUnread.value = read === null;
    dependents.value = read ?? [];
}

onMounted(() => {
    if (readingDependents.value) void readDependents();
});

/** Where focus goes when a read lands and takes Try again: the people's acknowledgement, or the first about the member's own plan. */
const firstAcknowledgement = () => block.value?.querySelector<HTMLInputElement>('input[type="checkbox"]');

const allowedToContinue = computed(() =>
    !readingDependents.value
    && !dependentsUnread.value
    && understood.services
    && understood.features
    && understood.removals
    && (dependents.value.length === 0 || understood.people),
);

const continueToNext = () => {
    if (allowedToContinue.value) {
        emit('goToPage', 3);
    }
};
</script>

<style lang="scss" scoped>
.refund-details-block {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    padding: var(--space-6);
}

.block-title {
    text-align: center;
}

.loss-cards {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
}

.loss-card-row {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);

    @screen lg {
        flex-direction: row;
    }

    .loss-card {
        @screen lg {
            flex: 1 1 0;
        }
    }
}

.loss-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--hairline);
    border-radius: var(--radius-md);
    background: var(--surface);
    box-shadow: var(--shadow-card);
}

// Amber, the strongest a zone gets: danger marks a badge or a dot, never a ground (DESIGN.md, There is no red ground).
.loss-card__band {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    background: var(--warning-wash);
    color: var(--warning-ink);
}

// The band's place while the people card is read: square, since the card's own corners clip it.
.loss-card__band-placeholder {
    border-radius: 0;
}

.loss-card__glyph {
    width: var(--space-8);
    height: var(--space-8);
}

.loss-card__body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
}

// The way to read again sits under the sentence that says the read failed, at its own width.
.loss-card__body--unread {
    align-items: flex-start;
}

.loss-card__title {
    margin: 0;
    color: var(--ink);
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    line-height: var(--leading-sm);
}

.loss-card__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    margin: 0;
    padding-left: var(--space-4);
    list-style: disc;
    color: var(--ink-body);
    font-size: var(--text-sm);
    line-height: var(--leading-sm);
}

.loss-card__note {
    margin: 0;
}

.loss-card__check {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: auto;
    padding-top: var(--space-2);
    color: var(--ink-body);
    font-size: var(--text-sm);
    line-height: var(--leading-sm);
    cursor: pointer;
}

.action-buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--space-3);
}
</style>
